<template>
  <div id="orders-profit">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title">
          {{ $t(`views.common.ordersProfit.title`) }}
        </h1>
        <v-spacer />
        <ShowCashButton
          v-show="!filters.positionType"
          class="mr-2 mt-2"
          :showCash="showCash"
          @showCashChanged="showCashChanged"
        />
        <ExportButton
          class="mt-2"
          :click="downloadCsv"
          :loading="exportLoading"
          :disabled="!items.length"
        />
      </v-card-title>
      <v-card-text class="mt-5 pa-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :server-items-length="totalItems"
          sort-by="date"
          :sort-desc="true"
          class="elevation-0"
          mobile-breakpoint="0"
          :loading="loading"
          :item-class="itemRowBackground"
          :footer-props="{
            'items-per-page-options': defaultPaginationItemsPerPageOptions,
          }"
          :items-per-page="10"
          :dense="user.preferences.denseTables"
          @update:options="updateOptions"
        >
          <template slot="body.prepend">
            <tr
              v-if="totalRow"
              :class="{
                'row-recreating': somePendingRecreatePosition,
                'row-total': !somePendingRecreatePosition,
              }"
            >
              <td class="text-right">
                <span />
              </td>
              <td>
                <div class="items-flex">
                  TOTAL
                  <div v-if="somePendingRecreatePosition" class="ml-3">
                    <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.textTotal')" />
                  </div>
                </div>
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.totalReceivedWithComissionBaseCurrency"
                  :currency="user.baseCurrency"
                />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.totalCostWithComissionBaseCurrency"
                  :currency="user.baseCurrency"
                />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.profitWithComissionBaseCurrency || 0"
                  :currency="user.baseCurrency"
                  :text-green="totalRow.profitWithComissionBaseCurrency > 0"
                  :text-red="totalRow.profitWithComissionBaseCurrency < 0"
                  :negative="totalRow.profitWithComissionBaseCurrency < 0"
                  strong
                />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.destinationRetentionBaseCurrency || 0"
                  :currency="user.baseCurrency"
                />
              </td>
            </tr>
          </template>

          <template v-slot:item.type="{ item }">
            <OrderTableTypeIcon :item="item" />
          </template>
          <template v-slot:item.position.ticker="{ item }">
            <div class="items-flex">
              <TableTicker :position="item.position" />
              <div
                v-if="item.position.corePendingRecreate || item.position.coreStatus !== 'recreated'"
                class="ml-3"
              >
                <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.text')" />
              </div>
              <template v-else-if="someNullExchangeRate(item)">
                <div class="ml-3 mt-1">
                  <WarningIcon
                    :text="$t(`views.dashboardDetail.orders.ordersByPosition.warning`)"
                  />
                </div>
              </template>
            </div>
          </template>
          <template v-slot:item.date="{ item }">
            <span v-if="item._id">{{ getDateUserFormat(item.date) }}</span>
            <span v-else></span>
          </template>
          <template v-slot:item.fifoNumber="{ item }">
            <span v-if="!item._id"></span>
            <currency v-else-if="item.fifoNumber" :value="item.fifoNumber" :decimals="7" />
            <span v-else></span>
          </template>
          <template v-slot:item.totalReceived="{ item }">
            <span v-if="!item._id"></span>
            <currency
              v-else-if="item.totalReceived"
              :value="item.totalReceived"
              :currency="item.position.currency"
            />
            <span v-else></span>
          </template>
          <template v-slot:item.totalReceivedWithComissionBaseCurrency="{ item }">
            <currency
              v-if="item.totalReceivedWithComissionBaseCurrency"
              :value="item.totalReceivedWithComissionBaseCurrency"
              :currency="user.baseCurrency"
            />
            <span v-else></span>
          </template>
          <template v-slot:item.totalCost="{ item }">
            <span v-if="!item._id"></span>
            <currency
              v-else-if="item.totalCost"
              :value="item.totalCost"
              :currency="item.position.currency"
            />
            <span v-else></span>
          </template>
          <template v-slot:item.totalCostWithComissionBaseCurrency="{ item }">
            <currency
              v-if="item.totalCostWithComissionBaseCurrency"
              :value="item.totalCostWithComissionBaseCurrency"
              :currency="user.baseCurrency"
            />
            <span v-else></span>
          </template>
          <template v-slot:item.profit="{ item }">
            <span v-if="!item._id"></span>
            <currency
              v-else
              :value="item.profit || 0"
              :currency="item.position.currency"
              :text-green="item.profit > 0"
              :text-red="item.profit < 0"
              :negative="item.profit < 0"
            />
          </template>
          <template v-slot:item.profitWithComissionBaseCurrency="{ item }">
            <currency
              :value="item.profitWithComissionBaseCurrency || 0"
              :currency="user.baseCurrency"
              :text-green="item.profitWithComissionBaseCurrency > 0"
              :text-red="item.profitWithComissionBaseCurrency < 0"
              :negative="item.profitWithComissionBaseCurrency < 0"
              strong
            />
          </template>
          <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
            <currency
              :value="item.destinationRetentionBaseCurrency || 0"
              :currency="user.baseCurrency"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { mapState } from 'vuex'
import { getDateUserFormat } from '@/lib/utils/date.js'
import { getOrderProfit, exportOrderProfitCsv } from '@/lib/api.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import OrderTableTypeIcon from '@/views/components/common/orders/OrderTableTypeIcon'
import Currency from '@/views/components/common/Currency'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import ExportButton from '@/views/components/common/ExportButton'
import ShowCashButton from '@/views/components/common/ShowCashButton'

export default {
  components: {
    OrderTableTypeIcon,
    Currency,
    TableTicker,
    WarningIcon,
    TableCoreRecreating,
    ExportButton,
    ShowCashButton,
  },
  mixins: [tableMixin],
  data: () => ({
    exportLoading: false,
    totalRow: null,
    loadTimeout: null,
    showCash: false,
    filters: [],
  }),
  computed: {
    ...mapState(['user']),
    defaultHeaders() {
      return [
        {
          text: '',
          value: 'type',
          sortable: false,
        },
        {
          text: this.$t(`views.common.ordersProfit.ticker`),
          value: 'position.ticker',
          sortable: false,
        },
        { text: this.$t('views.common.ordersProfit.date'), value: 'date' },
        {
          text: this.$t(`views.common.ordersProfit.fifoNumber`),
          value: 'fifoNumber',
          align: 'right',
        },
        {
          text: this.$t('views.common.ordersProfit.totalReceived'),
          value: 'totalReceived',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalReceivedWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalReceivedWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: this.$t('views.common.ordersProfit.totalCost'),
          value: 'totalCost',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalCostWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalCostWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: this.$t('views.common.ordersProfit.profit'),
          value: 'profit',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'profitWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.destinationRetention')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
      ]
    },
    somePendingRecreatePosition() {
      return this.items && this.items.some((item) => item.position.pendingRecreate)
    },
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    getDateUserFormat,
    someNullExchangeRate(item) {
      return (
        item.exchangeRate === null ||
        item.totalCostWithComissionBaseCurrency === null ||
        item.totalReceivedWithComissionBaseCurrency === null
      )
    },
    itemRowBackground(item) {
      if (item.position.pendingRecreate) return 'row-recreating'
      else if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    async loadSummary(filters) {
      this.filterElements(filters)
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = JSON.parse(
          JSON.stringify({
            positionType: !this.showCash ? { $ne: 'cash' } : undefined,
            ...this.filters,
          })
        )
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getOrderProfit({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)

        this.totalRow = response.data.totals

        clearTimeout(this.loadTimeout)
        if (this.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.loadItems()
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.setFirstPage()
      this.loadItems()
    },
    async downloadCsv() {
      this.exportLoading = true
      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await exportOrderProfitCsv({
          filter: {
            positionType: !this.showCash ? { $ne: 'cash' } : undefined,
            ...this.filters,
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(
          blob,
          `Filios - ${this.$t(`views.common.ordersProfit.title`)} ${nowFormatted}.csv`
        )
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#orders-profit {
  .items-flex {
    display: flex;
  }
}
</style>
